import React, { useEffect, useMemo, useState } from 'react';

export default function IkasLoyalty() {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [byChannel, setByChannel] = useState(true);
  const [channelRows, setChannelRows] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const refresh = async () => {
    try {
      setLoading(true);
      const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
      if (!brandId) { setSummary(null); return; }
      const headers = { 'X-Brand-ID': brandId };
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      // Demo fallback: fill loyalty numbers for demo brand
      const DEMO_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';
      if (brandId === DEMO_BRAND_ID) {
        const period = { startDate: startDate || '', endDate: endDate || '' };
        if (!byChannel) {
          setSummary({
            ok: true,
            period,
            summary: {
              total_revenue: 12500000,
              total_customers: 42000,
              // Customers and revenue realistic; orders set to plausible averages:
              // champions ~12 orders/customer; loyal ~5 orders/customer; returning ~2.3 orders/customer; one_time exactly 1
              champion: 2400, revenue_champion: 4200000, orders_champion: 28800,
              loyal: 9800, revenue_loyal: 3600000, orders_loyal: 49000,
              returning: 16800, revenue_returning: 3200000, orders_returning: 38640,
              one_time: 13000, revenue_one_time: 1500000, orders_one_time: 13000
            }
          });
          setChannelRows([]);
        } else {
          setChannelRows([
            {
              sales_channel_name: 'Online Store',
              total_revenue: 7200000,
              total_customers: 26000,
              champion: 1500, revenue_champion: 2600000, orders_champion: 18000,
              loyal: 6200, revenue_loyal: 2100000, orders_loyal: 31000,
              returning: 9800, revenue_returning: 1950000, orders_returning: 22540,
              one_time: 8500, revenue_one_time: 550000, orders_one_time: 8500
            },
            {
              sales_channel_name: 'Marketplace',
              total_revenue: 4100000,
              total_customers: 13000,
              champion: 700, revenue_champion: 1200000, orders_champion: 8400,
              loyal: 3000, revenue_loyal: 1100000, orders_loyal: 15000,
              returning: 5200, revenue_returning: 1400000, orders_returning: 11960,
              one_time: 4100, revenue_one_time: 400000, orders_one_time: 4100
            },
            {
              sales_channel_name: 'POS',
              total_revenue: 1200000,
              total_customers: 3000,
              champion: 200, revenue_champion: 400000, orders_champion: 2400,
              loyal: 600, revenue_loyal: 390000, orders_loyal: 3000,
              returning: 800, revenue_returning: 350000, orders_returning: 1840,
              one_time: 1400, revenue_one_time: 60000, orders_one_time: 1400
            }
          ]);
          setSummary({ period });
        }
        return;
      }
      if (!byChannel) {
        const url = params.toString()
          ? `${base}/api/ikas/loyalty/summary?${params.toString()}`
          : `${base}/api/ikas/loyalty/summary`;
        const res = await fetch(url, { credentials: 'include', headers });
        const js = await res.json().catch(() => ({}));
        if (res.ok && js?.ok) { setSummary(js); setChannelRows([]); }
        else { setSummary(null); setChannelRows([]); }
      } else {
        const url = params.toString()
          ? `${base}/api/ikas/loyalty/by-channel?${params.toString()}`
          : `${base}/api/ikas/loyalty/by-channel`;
        const res = await fetch(url, { credentials: 'include', headers });
        const js = await res.json().catch(() => ({}));
        if (res.ok && js?.ok) { setChannelRows(js.rows || []); setSummary({ period: js.period }); }
        else { setChannelRows([]); }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const periodText = useMemo(() => {
    const s = summary?.period?.startDate || startDate;
    const e = summary?.period?.endDate || endDate;
    if (s && e) return `${s} → ${e}`;
    if (s && !e) return `${s} → (today)`;
    if (!s && e) return `(all time) → ${e}`;
    return 'All time';
  }, [summary, startDate, endDate]);

  const segments = useMemo(() => {
    const s = summary?.summary || {};
    const totalRevenue = Number(s.total_revenue || 0);
    const totalCustomers = Number(s.total_customers || 0);
    const build = (key, title, count, rev, ords, color) => {
      const customers = Number(count || 0);
      const revenue = Number(rev || 0);
      const orders = Number(ords || 0);
      const share = totalRevenue > 0 ? (revenue / totalRevenue) : 0;
      const avgRev = customers > 0 ? (revenue / customers) : 0;
      const avgOrders = customers > 0 ? (orders / customers) : 0;
      const customerShare = totalCustomers > 0 ? (customers / totalCustomers) : 0;
      return { key, title, customers, revenue, orders, share, customerShare, avgRev, avgOrders, color };
    };
    return [
      build('champion', 'Champions (8+)', s.champion, s.revenue_champion, s.orders_champion, 'from-yellow-500 to-orange-500'),
      build('loyal', 'Loyal (4–7)', s.loyal, s.revenue_loyal, s.orders_loyal, 'from-emerald-500 to-teal-500'),
      build('returning', 'Returning (2–3)', s.returning, s.revenue_returning, s.orders_returning, 'from-blue-500 to-indigo-500'),
      build('one_time', 'Explorers (1)', s.one_time, s.revenue_one_time, s.orders_one_time, 'from-gray-400 to-gray-500'),
    ];
  }, [summary]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 relative">
      {loading ? (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex items-center gap-3 text-gray-700">
            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        </div>
      ) : null}

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/ikaslogoicon.png"
              alt="Ikas"
              className="w-10 h-10 object-contain rounded-lg"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Loyalty Segments</h1>
              <p className="text-sm text-gray-500">Order-frequency based loyalty buckets.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1 text-sm border rounded-lg"
            />
            <span className="text-gray-400">→</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-1 text-sm border rounded-lg"
            />
            <button
              onClick={refresh}
              className={`px-3 py-1.5 text-sm rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors ${loading ? 'opacity-50' : ''}`}
              disabled={loading}
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Period */}
      <div className="text-sm text-gray-600">
        Period: <span className="font-medium text-gray-800">{periodText}</span>
      </div>

      {/* Segments (stacked) */}
      <div className="space-y-4">
        {!byChannel && segments.map((seg) => (
          <div key={seg.key} className="rounded-2xl border shadow-sm overflow-hidden">
            <div className="bg-black text-white p-4">
              <div className="text-lg font-semibold">{seg.title}</div>
              <div className="text-xs opacity-90">
                Customers: {seg.customers.toLocaleString()} • Revenue Share: {Math.round(seg.share * 100).toString()}% • Customer Share: {Math.round(seg.customerShare * 100).toString()}%
              </div>
            </div>
            <div className="bg-white p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-500">Total Revenue</div>
                <div className="text-lg font-semibold text-gray-900">
                  {Math.round(seg.revenue).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Revenue per Customer</div>
                <div className="text-lg font-semibold text-gray-900">
                  {Math.round(seg.avgRev).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Total Orders</div>
                <div className="text-lg font-semibold text-gray-900">{seg.orders.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Orders per Customer</div>
                <div className="text-lg font-semibold text-gray-900">
                  {Math.round(seg.avgOrders).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        ))}
        {byChannel && channelRows.map((row) => {
          const totalRevenue = Number(row.total_revenue || 0);
          const totalCustomers = Number(row.total_customers || 0);
          const mkSeg = (title, cnt, rev, ords) => {
            const customers = Number(cnt || 0);
            const revenue = Number(rev || 0);
            const orders = Number(ords || 0);
            const share = totalRevenue > 0 ? (revenue / totalRevenue) : 0;
            const customerShare = totalCustomers > 0 ? (customers / totalCustomers) : 0;
            const avgRev = customers > 0 ? revenue / customers : 0;
            const avgOrders = customers > 0 ? orders / customers : 0;
            return { title, customers, revenue, orders, share, customerShare, avgRev, avgOrders };
          };
          const channelSegs = [
            mkSeg('Champions (8+)', row.champion, row.revenue_champion, row.orders_champion),
            mkSeg('Loyal (4–7)', row.loyal, row.revenue_loyal, row.orders_loyal),
            mkSeg('Returning (2–3)', row.returning, row.revenue_returning, row.orders_returning),
            mkSeg('Explorers (1)', row.one_time, row.revenue_one_time, row.orders_one_time),
          ];
          return (
            <div key={row.sales_channel_name} className="rounded-2xl border shadow-sm overflow-hidden">
              <div className="bg-black text-white p-4">
                <div className="text-lg font-semibold">{row.sales_channel_name}</div>
                <div className="text-xs opacity-90">
                  Customers: {Number(row.total_customers || 0).toLocaleString()} • Revenue: {Math.round(totalRevenue).toLocaleString()}
                </div>
              </div>
              <div className="bg-white p-4 space-y-3">
                {channelSegs.map((seg) => (
                  <div key={seg.title} className="border rounded-xl">
                    <div className="px-4 py-2 border-b font-semibold text-gray-900">{seg.title}</div>
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Customers</div>
                        <div className="text-lg font-semibold text-gray-900">{seg.customers.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Total Revenue</div>
                        <div className="text-lg font-semibold text-gray-900">{Math.round(seg.revenue).toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Revenue per Customer</div>
                        <div className="text-lg font-semibold text-gray-900">{Math.round(seg.avgRev).toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Orders per Customer</div>
                        <div className="text-lg font-semibold text-gray-900">{Math.round(seg.avgOrders).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


